/**
 * Chakra UI v3 Combobox Component
 *
 * Re-export Chakra UI's built-in Combobox with proper typing
 * Custom Content wrapper prevents transparent dropdown backgrounds
 *
 * RULE 15: Ark UI components support children at runtime but TypeScript
 * definitions don't include them. We add proper type overrides here.
 */

'use client';

import React from 'react';
import { Combobox as ChakraCombobox, createListCollection, Box } from '@chakra-ui/react';

/**
 * RULE 15: Type overrides for Ark UI Combobox components
 * Ark UI components support children at runtime but TypeScript definitions don't include them
 * We create proper type wrappers that add children support while maintaining all other props
 */

// Helper type to add children to component props
type WithChildren<T> = T & { children?: React.ReactNode };

// Type-safe wrappers that add children support
// RULE 15: Ark UI's TypeScript definitions are incomplete - they don't expose all runtime props
// We use Record<string, unknown> to allow all Ark UI props to pass through while maintaining type safety
type ArkUIProps = Record<string, unknown>;

/**
 * Custom Combobox.Content wrapper with automatic background
 * RULE 15: DRY compliance - prevents transparent dropdown backgrounds
 * Matches modal Dialog.Content pattern for consistency
 */
const ComboboxContent = React.forwardRef<HTMLDivElement, WithChildren<ArkUIProps>>((props, ref) => {
  const { children, ...rest } = props;
  // Cast to unknown first to allow children prop
  const ContentComponent = ChakraCombobox.Content as unknown as React.FC<
    WithChildren<Record<string, unknown> & { ref?: React.Ref<HTMLDivElement> }>
  >;
  return (
    <ContentComponent ref={ref} {...rest}>
      <Box background="bg.default" borderRadius="md" boxShadow="lg">
        {children as React.ReactNode}
      </Box>
    </ContentComponent>
  );
});

ComboboxContent.displayName = 'ComboboxContent';

const ComboboxControl = ChakraCombobox.Control as unknown as React.FC<WithChildren<ArkUIProps>>;
const ComboboxInput = ChakraCombobox.Input as unknown as React.FC<WithChildren<ArkUIProps>>;
const ComboboxTrigger = ChakraCombobox.Trigger as unknown as React.FC<WithChildren<ArkUIProps>>;
const ComboboxPositioner = ChakraCombobox.Positioner as unknown as React.FC<
  WithChildren<ArkUIProps>
>;
const ComboboxItem = ChakraCombobox.Item as unknown as React.FC<WithChildren<ArkUIProps>>;
const ComboboxItemText = ChakraCombobox.ItemText as unknown as React.FC<WithChildren<ArkUIProps>>;
const ComboboxItemIndicator = ChakraCombobox.ItemIndicator as unknown as React.FC<
  WithChildren<ArkUIProps>
>;

export const Combobox = {
  Root: ChakraCombobox.Root,
  Label: ChakraCombobox.Label,
  Control: ComboboxControl,
  Input: ComboboxInput,
  Trigger: ComboboxTrigger,
  IndicatorGroup: ChakraCombobox.IndicatorGroup,
  ClearTrigger: ChakraCombobox.ClearTrigger,
  Positioner: ComboboxPositioner,
  Content: ComboboxContent, // Custom wrapped version with children support
  Empty: ChakraCombobox.Empty,
  Item: ComboboxItem,
  ItemGroup: ChakraCombobox.ItemGroup,
  ItemText: ComboboxItemText,
  ItemIndicator: ComboboxItemIndicator,
};

// Re-export createListCollection utility
export { createListCollection };

export type {
  ComboboxRootProps,
  ComboboxLabelProps,
  ComboboxControlProps,
  ComboboxInputProps,
  ComboboxTriggerProps,
  ComboboxIndicatorGroupProps,
  ComboboxClearTriggerProps,
  ComboboxPositionerProps,
  ComboboxContentProps,
  ComboboxEmptyProps,
  ComboboxItemProps,
  ComboboxItemGroupProps,
  ComboboxItemTextProps,
  ComboboxItemIndicatorProps,
} from '@chakra-ui/react';
